"use client";

import type React from "react";

import { useCaptureExposures } from "@/app/_analytics/useCaptureExposures";
import { useCapturePageViews } from "@/app/_analytics/useCapturePageViews";
import { SaveUTMs } from "@/app/_analytics/utmHandler";
import { SupportAgentConfigProvider } from "@/app/_components/SupportAgent/ConfigProvider";
import { FontPreloader } from "@/app/_utilities/FontPreloader";
import { ThirdPartyScripts } from "@/app/_utilities/ThirdPartyScripts";
import { useHandleCCPA } from "@/app/_utilities/ccpa";
import { usePartnersStack } from "@/app/_utilities/partnerstack";
import { DefaultStructuredData } from "@/app/_utilities/structuredData";
import { LazyMotion } from "motion/react";
import { Suspense } from "react";

import { PreviousPathProvider } from "./PreviousPathProvider";
import { ActivePlayerProvider } from "./active-player";
import { HCaptchaProvider } from "./hcaptcha/HCaptchaProvider";
import { PHProvider } from "./posthog";
import { PrefetchAppRoutes } from "./prefetchAppRoutes";
import { LazyToaster } from "./toast";
import { RouterProvider } from "react-aria-components";
import { useRouter } from "@/i18n/navigation";
import { AnchoredToastProvider } from "./AnchoredToastProvider";

export const PageViews = () => {
  useCapturePageViews();
  return <></>;
};

export const ExposureTracking = () => {
  useCaptureExposures();
  return <></>;
};

export const CCPAOptOut = () => {
  useHandleCCPA();
  return <></>;
};

export const PartnerStack = () => {
  usePartnersStack();
  return null;
};

const loadFeatures = () => import("./lazyMotion").then(mod => mod.default);

// Configure the type of the `routerOptions` prop on all React Aria components.
// https://react-aria.adobe.com/frameworks
declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: NonNullable<Parameters<ReturnType<typeof useRouter>["push"]>[1]>;
  }
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const router = useRouter();

  return (
    <>
      <PrefetchAppRoutes />
      <DefaultStructuredData />
      <FontPreloader />
      <Suspense>
        <SaveUTMs />
      </Suspense>
      <CCPAOptOut />
      <ThirdPartyScripts />
      <PartnerStack />
      <LazyMotion features={loadFeatures} strict>
        <ActivePlayerProvider>
          <PreviousPathProvider>
            <PHProvider>
              <Suspense>
                <PageViews />
              </Suspense>
              <ExposureTracking />
              <SupportAgentConfigProvider>
                <RouterProvider navigate={router.push}>{children}</RouterProvider>
              </SupportAgentConfigProvider>
            </PHProvider>
          </PreviousPathProvider>
        </ActivePlayerProvider>
      </LazyMotion>
      <LazyToaster />
      <HCaptchaProvider />
      <AnchoredToastProvider />
    </>
  );
}
